import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { customerApi } from './CustomerApi'
import { CustomerSearchRequest, CustomerSearchModel, UpdateCustomerRequest, CreateCustomerRequest } from '../models'

interface CustomerState {
    customers: CustomerSearchModel[]
    selectedCustomer: CustomerSearchModel | null
    totalRows: number
    loading: boolean
    error: string | null
}

const initialState: CustomerState = {
    customers: [],
    selectedCustomer: null,
    totalRows: 0,
    loading: false,
    error: null,
}

export const fetchCustomerData = createAsyncThunk(
    'customers/fetchCustomerData',
    async (params: CustomerSearchRequest) => {
        const response = await customerApi.searchCustomer(params)
        return response.data
    }
)

export const fetchCustomerById = createAsyncThunk(
    'customers/fetchCustomerById',
    async (customerId: string) => {
        const response = await customerApi.getCustomerById(customerId)
        return response.data
    }
)

export const updateCustomer = createAsyncThunk(
    'customers/updateCustomer',
    async (params: { customerId: string, payload: UpdateCustomerRequest }, { rejectWithValue }) => {
        try {
            const response = await customerApi.updateCustomer(params.customerId, params.payload)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data || { message: err.message })
        }
    }
)

export const createCustomer = createAsyncThunk(
    'customers/createCustomer',
    async (payload: CreateCustomerRequest, { rejectWithValue }) => {
        try {
            const response = await customerApi.createCustomer(payload)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data || { message: err.message })
        }
    }
)

export const toggleBlockCustomer = createAsyncThunk(
    'customers/toggleBlockCustomer',
    async (params: { customerId: string, isBlocked: boolean }, { rejectWithValue }) => {
        try {
            const response = await customerApi.toggleBlockCustomer(params.customerId, params.isBlocked)
            return response.data
        } catch (err: any) {
            return rejectWithValue(err.response?.data || { message: err.message })
        }
    }
)

const customerSlice = createSlice({
    name: 'customers',
    initialState,
    reducers: {
        toggleCustomerStatus: (state, action) => {
            const customer = state.customers.find(c =>
                c.customerId === action.payload || c.id === action.payload
            )
            if (customer) {
                customer.isActive = !customer.isActive
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomerData.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchCustomerData.fulfilled, (state, action) => {
                state.loading = false
                state.customers = action.payload.rows
                state.totalRows = action.payload.pagination.totalRows
            })
            .addCase(fetchCustomerData.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message || 'Failed to fetch customers'
            })
            .addCase(fetchCustomerById.pending, (state) => {
                state.loading = true
                state.error = null
                state.selectedCustomer = null
            })
            .addCase(fetchCustomerById.fulfilled, (state, action) => {
                state.loading = false
                state.selectedCustomer = action.payload
            })
            .addCase(fetchCustomerById.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message || 'Failed to fetch customer details'
            })
            .addCase(updateCustomer.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(updateCustomer.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(updateCustomer.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message || 'Failed to update customer'
            })
            .addCase(createCustomer.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(createCustomer.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(createCustomer.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message || 'Failed to create customer'
            })
            .addCase(toggleBlockCustomer.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(toggleBlockCustomer.fulfilled, (state, action) => {
                state.loading = false
                if (state.selectedCustomer && state.selectedCustomer.customerId === action.meta.arg.customerId) {
                    state.selectedCustomer.isBlocked = action.meta.arg.isBlocked
                }
            })
            .addCase(toggleBlockCustomer.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message || 'Failed to toggle block customer'
            })
    },
})

export const customerActions = { ...customerSlice.actions, fetchCustomerData, fetchCustomerById, updateCustomer, toggleBlockCustomer, createCustomer }
export const customerReducer = customerSlice.reducer
